import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { DollarSign, Calendar, History, Clock } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Payment {
  id: string;
  monto: number;
  fecha: Date;
  notas?: string;
  metodo?: string;
}

interface PaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  provider: {
    id: string;
    provider: { 
      nombre: string; 
      tipo: string;
    };
    descripcion_servicio: string;
    precio: number;
    pagado: number;
  } | null;
  onPaymentRegistered: (providerId: string, newAmount: number, notes?: string) => void;
}

export const PaymentModal: React.FC<PaymentModalProps> = ({
  open,
  onOpenChange,
  provider,
  onPaymentRegistered
}) => {
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [payments, setPayments] = useState<Payment[]>([]);
  const { toast } = useToast();

  // Mock payment history - this would come from the database
  useEffect(() => {
    if (provider) {
      // Simulate loading payment history
      const mockPayments: Payment[] = [
        {
          id: '1',
          monto: 10000,
          fecha: new Date('2024-01-15'),
          notas: 'Anticipo del 40%',
          metodo: 'Transferencia'
        },
        {
          id: '2',
          monto: 5000,
          fecha: new Date('2024-02-01'),
          notas: 'Segundo pago',
          metodo: 'Efectivo'
        }
      ];
      setPayments(mockPayments);
    }
  }, [provider]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!provider) return;

    const amount = parseFloat(paymentAmount);
    if (isNaN(amount) || amount <= 0) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Por favor ingresa una cantidad válida"
      });
      return;
    }

    const newTotal = provider.pagado + amount;
    if (newTotal > provider.precio) {
      toast({
        variant: "destructive",
        title: "Error", 
        description: "El monto total no puede exceder el precio del servicio"
      });
      return;
    }

    setIsProcessing(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Add new payment to history
      const newPayment: Payment = {
        id: Date.now().toString(),
        monto: amount,
        fecha: new Date(),
        notas: paymentNotes,
        metodo: paymentMethod
      };
      setPayments(prev => [...prev, newPayment]);
      
      onPaymentRegistered(provider.id, newTotal, paymentNotes);
      
      toast({
        title: "Pago registrado",
        description: `Se registró un pago de ${formatCurrency(amount)}`
      });

      setPaymentAmount('');
      setPaymentNotes('');
      setPaymentMethod('');
      onOpenChange(false);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo registrar el pago"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  if (!provider) return null;

  const remainingAmount = provider.precio - provider.pagado;
  const paymentPercentage = (provider.pagado / provider.precio) * 100;

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <DollarSign className="w-5 h-5 mr-2" />
            Registrar Pago
          </DialogTitle>
          <DialogDescription>
            Registra pagos realizados a este proveedor y consulta el historial de pagos anteriores.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Provider Info */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium">{provider.provider.nombre}</h3>
            <p className="text-sm text-gray-600">{provider.provider.tipo}</p>
            <p className="text-sm text-gray-600 mt-1">{provider.descripcion_servicio}</p>
          </div>

          {/* Payment Status */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Precio Total:</span>
              <span className="font-bold">{formatCurrency(provider.precio)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Pagado:</span>
              <span className="text-green-600">{formatCurrency(provider.pagado)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Saldo Pendiente:</span>
              <span className="text-red-600 font-bold">{formatCurrency(remainingAmount)}</span>
            </div>
            
            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div 
                className="bg-green-600 h-2 rounded-full transition-all duration-300" 
                style={{ width: `${Math.min(paymentPercentage, 100)}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 text-center">{paymentPercentage.toFixed(1)}% completado</p>
          </div>

          {/* Payment History and New Payment Form in Columns */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Payment History - Left Column */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <History className="w-4 h-4" />
                <h4 className="font-medium">Historial de Pagos ({payments.length})</h4>
              </div>
              
              <div className="max-h-64 overflow-y-auto space-y-2 pr-2">
                {payments.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 text-sm">
                    <DollarSign className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p>No hay pagos registrados</p>
                  </div>
                ) : (
                  payments.map((payment) => (
                    <div key={payment.id} className="flex justify-between items-start p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-3 h-3 text-green-600" />
                          <span className="font-medium text-green-600">
                            {formatCurrency(payment.monto)}
                          </span>
                          {payment.metodo && (
                            <Badge variant="outline" className="text-xs">
                              {payment.metodo}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
                          <Clock className="w-3 h-3" />
                          {formatDate(payment.fecha)}
                        </div>
                        {payment.notas && (
                          <p className="text-xs text-gray-600 mt-1">{payment.notas}</p>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Payment Form - Right Column */}
            <div className="space-y-3">
              <h4 className="font-medium">Registrar Nuevo Pago</h4>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="payment-amount">Monto del Pago</Label>
                  <Input
                    id="payment-amount"
                    type="number"
                    step="0.01"
                    min="0"
                    max={remainingAmount}
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    placeholder="0.00"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Máximo: {formatCurrency(remainingAmount)}
                  </p>
                </div>

                <div>
                  <Label htmlFor="payment-method">Método de Pago</Label>
                  <Input
                    id="payment-method"
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    placeholder="Ej: Transferencia, Efectivo, Cheque..."
                  />
                </div>

                <div>
                  <Label htmlFor="payment-notes">Notas del Pago (Opcional)</Label>
                  <Textarea
                    id="payment-notes"
                    value={paymentNotes}
                    onChange={(e) => setPaymentNotes(e.target.value)}
                    placeholder="Referencia, observaciones, etc..."
                    rows={2}
                  />
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={isProcessing}>
                    {isProcessing ? "Procesando..." : "Registrar Pago"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};